import type { Score, SimSession, SimulatorSide, Submission } from "@/lib/api";

/**
 * Ordering runs by what the server said they were worth.
 *
 * The rubric is evaluated on the server only (`source/rubric.py`); every submission,
 * session and leaderboard row arrives with its `Score`. What remains for the site is to
 * put runs in the leaderboard's order -- points, then SPL, then fewer steps, then smaller
 * distance, then who got there first -- when a page has a list of them and needs "which
 * one counts".
 */

/** What ranking needs to know about a run: whether it was judged, and what it scored. */
export interface Run {
  status: Submission["status"];
  score: Score | null;
  spl: number | null;
  nav_steps: number | null;
  /** Null for a miss, whose distance the server keeps to itself. */
  arrival: { distance_m: number } | null;
  submitted_at: number;
}

function judged(run: Run): boolean {
  return run.status === "done" && run.score !== null;
}

/**
 * Leaderboard ordering for two runs; negative when `a` ranks higher.
 *
 * Mirrors `scoring.rank_key`: points first, then SPL, then smaller distance, then the
 * earlier submission. An upload has no SPL -- its maze has no oracle route -- so there, and
 * only there, fewer steps break the tie before distance. A miss has no distance to compare
 * and, having not arrived, no steps worth comparing either; misses sort among themselves by
 * time. A run that is not `done` never outranks one that is.
 */
export function compareRuns(a: Run, b: Run): number {
  const ja = judged(a);
  const jb = judged(b);
  if (!ja || !jb) return ja === jb ? 0 : ja ? -1 : 1;
  const ra = a.score!.reached;
  const rb = b.score!.reached;
  return (
    b.score!.points - a.score!.points ||
    (b.spl ?? 0) - (a.spl ?? 0) ||
    stepsRung(a, ra) - stepsRung(b, rb) ||
    (a.arrival?.distance_m ?? Infinity) - (b.arrival?.distance_m ?? Infinity) ||
    a.submitted_at - b.submitted_at
  );
}

/** Steps as a tie-break rung: only for a run without an SPL, and only if it arrived. */
function stepsRung(run: Run, reached: boolean): number {
  if (run.spl !== null && run.spl !== undefined) return 0;
  return reached ? (run.nav_steps ?? Infinity) : Infinity;
}

/** The run the leaderboard would pick from this list, or null if none is scored. */
export function bestRun<T extends Run>(runs: readonly T[]): T | null {
  let best: T | null = null;
  for (const run of runs) {
    if (!judged(run)) continue;
    if (best === null || compareRuns(run, best) < 0) best = run;
  }
  return best;
}

/**
 * A session as a `Run`, so `bestRun` can pick from a list of attempts.
 *
 * Only a `scored` session was judged; every other status reads as unscored, the way a
 * `failed` submission does, and never outranks one that was.
 */
export function sessionRun<S extends SimSession>(session: S): Run & { session: S } {
  const scored = session.status === "scored" && session.result !== null;
  return {
    session,
    status: scored ? "done" : "failed",
    score: scored ? session.result!.score : null,
    spl: scored ? session.result!.spl : null,
    nav_steps: scored ? session.result!.nav_steps : null,
    arrival: scored ? session.result!.arrival : null,
    submitted_at: session.ended_at ?? session.started_at,
  };
}

/** Points without a trailing ".0", since rubrics are almost always whole numbers. */
export function formatPoints(points: number): string {
  return Number.isInteger(points) ? String(points) : points.toFixed(1);
}

/** SPL to two places: "0.83". */
export function formatSpl(spl: number | null): string {
  return spl === null ? "—" : spl.toFixed(2);
}

/**
 * What the create dialog offers: distance 3/2/1, heading 3/2/1, SPL 4/3/2/1 -- ten points,
 * and a goal that asks for the heading too, since the target is given as views and view 0
 * is the way to face. The same text as `rubric.SUGGESTED_TEXT` on the server; heading and
 * SPL are dropped for a client-simulated challenge, which cannot measure them.
 */
export function suggestedRubric(simulator: SimulatorSide): string {
  const rules = [
    "",
    "# Distance",
    "distance <= 0.1 -> 3",
    "distance <= 0.15 -> 2",
    "distance <= 0.2 -> 1",
  ];
  if (simulator === "client") return ["goal: distance <= 0.2 m", ...rules].join("\n") + "\n";
  return [
    "goal: distance <= 0.2 m and heading <= 20",
    ...rules,
    "",
    "# Heading",
    "heading <= 10 -> 3",
    "heading <= 15 -> 2",
    "heading <= 20 -> 1",
    "",
    "# SPL",
    "spl >= 0.95 -> 4",
    "spl >= 0.85 -> 3",
    "spl >= 0.75 -> 2",
    "spl >= 0.5 -> 1",
    "",
  ].join("\n");
}
